import React, { useEffect, useRef, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import styled from "@emotion/styled";
import BuildDropdown from "./BuildDropdown";
import { getBuildingList } from "apis/meetings/meeting-room";
import { TBuilding } from "types/building.types";
import { addBuilding } from "apis/resources/building";
import { useToast } from "hook/useToast";

interface RoomBuildingFieldProps {
  selectedBuilding?: string; //* INFO: 건물 이름
  selectedBuildingId?: string; //* INFO: 건물 id
  onChange: (buildingName?: string, buildingId?: string) => void; //* INFO: 건물 이름과 id를 받아와서 저장하는 함수
  disabled?: boolean; //* INFO: 비활성화 여부
  onUpdate?: () => void; //* INFO: 건물 리스트 업데이트 함수
  buildingList?: (TBuilding | null)[]; //* INFO: 건물 리스트
  onChangeBuildingList?: (value: (TBuilding | null)[]) => void; //* INFO: 건물 리스트를 업데이트하는 함수
}

export const RoomBuildingField: React.FC<RoomBuildingFieldProps> = ({
  selectedBuilding,
  selectedBuildingId,
  onChange,
  disabled = false,
  onUpdate,
  buildingList = [],
  onChangeBuildingList,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const buildingDropdownRef = useRef(false);
  const { showToast } = useToast();

  useEffect(() => {
    getBuildingList().then((res) => {
      onChangeBuildingList?.(res.data ?? []);
    });
  }, []);

  const handleRemoveBuilding = () => {
    if (disabled) return;
    onChange("", "");
  };

  const updateBuildingList = async (type: string, updateId: string) => {
    const buildingList = await getBuildingList();
    if (buildingList.data) {
      onChangeBuildingList?.(buildingList.data);
    }

    //* INFO: 선택된 건물이 수정되었을 때, 수정된 건물로 변경해준다.
    if (selectedBuildingId === updateId) {
      let updatedBuildingName = buildingList.data?.find((building) => building?.id === updateId)?.name;
      onChange(updatedBuildingName ?? "", selectedBuildingId);
    } else {
      //* INFO: 선택된 건물이 삭제되었을 때, 선택된 건물을 초기화해준다.
      onChange(undefined, selectedBuildingId);
    }
    onUpdate?.();
  };

  const handleOpenDropdown = () => {
    setIsOpenDropdown(true);
  };

  const handleAddBuilding = async (value: string) => {
    if (value !== "") {
      const res = await addBuilding(value, "MILE");
      if (res.data) {
        getBuildingList().then((res) => {
          onChangeBuildingList?.(res.data ?? []);
        });
      } else {
        showToast(res.message ?? "건물을 추가할 수 없습니다.", "error");
      }
    }
  };

  const handleDropdownClick = () => {
    buildingDropdownRef.current = true;
  };

  const buildingDropdownItemList = buildingList
    .filter((item) => item !== null)
    .map((item) => {
      return {
        name: item.name,
        id: item.id,
        provider: item.provider as "MILE" | "GOOGLE",
      };
    });

  return (
    <Container>
      <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
        건물
      </UI.Typography>
      <div style={{ position: "relative" }} onFocus={handleOpenDropdown}>
        <UI.InputGroup.TextField
          disabled={disabled}
          placeholder="회의실 위치 건물"
          showPrefixIcon
          prefixIcon="office"
          value={selectedBuilding ?? ""}
          disabledFocus
          suffixIcon="removeCircle"
          showSuffixIcon={selectedBuilding ? true : false}
          onClickSuffixIcon={handleRemoveBuilding}
        />
        {isOpenDropdown && (
          <BuildDropdown
            onUpdateBuildInfo={updateBuildingList}
            open={isOpenDropdown}
            setOpen={handleOpenDropdown}
            type="building"
            onAddOption={handleAddBuilding}
            selected={selectedBuilding}
            onDropdownClick={handleDropdownClick}
            data={buildingDropdownItemList}
            onChange={(id, name) => {
              if (name === selectedBuilding) {
                handleRemoveBuilding();
                return;
              } else {
                onChange(name, id);
                setIsOpenDropdown(false);
              }
            }}
          />
        )}
      </div>
    </Container>
  );
};

export default React.memo(RoomBuildingField);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;
